/**
 * ContactPortfolioPage
 *
 * Admin mirror of the investor app Portfolio tab.
 * Route: /contacts/:id/portfolio
 */

import ApartmentIcon from '@mui/icons-material/Apartment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ContactNotesSection from './ContactOverview/ContactNotesSection';
import ContactOverviewHeader from './ContactOverview/ContactOverviewHeader';
import ContactPortfolioActiveFundsSection from './ContactPortfolio/ContactPortfolioActiveFundsSection';
import ContactPortfolioClosedFundsSection from './ContactPortfolio/ContactPortfolioClosedFundsSection';
import ContactPortfolioEntitiesSection from './ContactPortfolio/ContactPortfolioEntitiesSection';
import ContactPortfolioHeroCard from './ContactPortfolio/ContactPortfolioHeroCard';
import ContactPortfolioPerformanceSection from './ContactPortfolio/ContactPortfolioPerformanceSection';
import ContactPortfolioSectionCard from './ContactPortfolio/ContactPortfolioSectionCard';
import ContactPortfolioTagsSection from './ContactPortfolio/ContactPortfolioTagsSection';
import { useContactPortfolioData } from './ContactPortfolio/useContactPortfolioData';

export default function ContactPortfolioPage() {
  const { id: contactId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    contact,
    contactLoading,
    contactError,
    summary,
    breakdown,
    overviewMetrics,
    summaryLoading,
    selectedFundIds,
    dataType,
    setDataType,
    combinedView,
    setCombinedView,
    toggleFundSelection,
    investmentsLoading,
    portfolioInvestors,
    breakdownByFundId,
    hasChartData,
    combinedData,
    multiData,
    tags,
    tagsLoading,
    entities,
    entitiesLoading,
  } = useContactPortfolioData(contactId);

  if (contactLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (contactError || !contact) {
    return (
      <Box p={3}>
        <Alert severity="error">{contactError ?? 'Contact not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/contacts')}>
          Back to Contacts
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
      <ContactOverviewHeader contact={contact} onEdit={() => navigate(`/contacts/${contactId}`)} />

      <ContactPortfolioHeroCard
        summary={summary}
        overviewMetrics={overviewMetrics}
        loading={summaryLoading}
      />

      <ContactPortfolioPerformanceSection
        loading={summaryLoading}
        hasChartData={hasChartData}
        breakdown={breakdown}
        selectedFundIds={selectedFundIds}
        dataType={dataType}
        combinedView={combinedView}
        combinedData={combinedData}
        multiData={multiData}
        onDataTypeChange={setDataType}
        onCombinedViewChange={setCombinedView}
        onToggleFund={toggleFundSelection}
      />

      <ContactPortfolioTagsSection tags={tags} loading={tagsLoading} />

      <ContactPortfolioEntitiesSection entities={entities} loading={entitiesLoading} />

      <ContactPortfolioActiveFundsSection
        loading={investmentsLoading}
        portfolioInvestors={portfolioInvestors}
        breakdownByFundId={breakdownByFundId}
        navigate={navigate}
      />

      <ContactPortfolioClosedFundsSection
        contactId={contactId!}
        completeFunds={summary?.completeFunds ?? 0}
        navigate={navigate}
      />

      <ContactPortfolioSectionCard
        title="Notes"
        icon={<AssignmentIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
      >
        <ContactNotesSection contactExternalId={contactId!} />
      </ContactPortfolioSectionCard>

      <Box sx={{ textAlign: 'center', mt: 1, mb: 3 }}>
        <Button
          variant="text"
          size="small"
          startIcon={<ApartmentIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate(`/contacts/${contactId}/opportunities`)}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          View available new fund opportunities
        </Button>
      </Box>
    </Box>
  );
}
